import StraightenIcon from "@mui/icons-material/Straighten";
import { Box, Stack, Typography } from "@mui/material";

import { RoutePointDetail } from "components/Shipment/components/RoutePointDetail";
import { ShipmentDetail } from "components/Shipment/components/ShipmentDetail";
import { SuggestedNavigationRouteMap } from "components/ShipmentOverviewTab/components/RouteTab/components/SuggestedNavigationRouteMap";
import {
  getRouteDestination,
  getRouteOrigin,
  isRouteCreatedAfterNavigationRollout,
  meterToKilometer,
  meterToMile,
  removeDecimalPoint,
} from "utils";

interface Props {
  shipment: PublicShipment;
}

interface PointCoords {
  longitude: number;
  latitude: number;
  point_number: number;
}

function toCoords(point: RoutePoint): PointCoords | null {
  const { latitude, longitude } = point.point_location;
  if (latitude == null || longitude == null) {
    return null;
  }
  const latitudeNumber = Number(latitude);
  const longitudeNumber = Number(longitude);
  if (!Number.isFinite(latitudeNumber) || !Number.isFinite(longitudeNumber)) {
    return null;
  }
  return {
    latitude: latitudeNumber,
    longitude: longitudeNumber,
    point_number: point.point_number,
  };
}

export function PublicShipmentRoute(props: Props) {
  const { shipment } = props;
  const { route_points: routePoints, navigation } = shipment;

  if (routePoints.length === 0) {
    return (
      <Stack>
        <Typography align="center">No route points found.</Typography>
      </Stack>
    );
  }

  const origin = getRouteOrigin(routePoints);
  const destination = getRouteDestination(routePoints);
  const midPoints = routePoints
    .filter(
      (routePoint) =>
        routePoint.stop_type !== "ORIGIN" &&
        routePoint.stop_type !== "DESTINATION",
    )
    .sort((a, b) => a.point_number - b.point_number);

  const originCoords = origin != null ? toCoords(origin) : null;
  const destinationCoords = destination != null ? toCoords(destination) : null;
  const midPointCoords = midPoints
    .map(toCoords)
    .filter((pointCoords): pointCoords is PointCoords => pointCoords !== null);

  const canRenderMap =
    navigation != null && originCoords != null && destinationCoords != null;

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column" width="100%">
        {origin != null && (
          <RoutePointDetail routePoint={origin} displayRoutePointDates />
        )}
        {midPoints.map((routePoint) => (
          <Box key={routePoint.point_number}>
            <RoutePointDetail routePoint={routePoint} displayRoutePointDates />
          </Box>
        ))}
        {destination != null && (
          <RoutePointDetail routePoint={destination} displayRoutePointDates />
        )}
      </Stack>

      {canRenderMap ? (
        <Stack direction="column">
          <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
            <ShipmentDetail
              icon={<StraightenIcon />}
              title="Route Distance"
              text={`${removeDecimalPoint(meterToMile(navigation.distance_meters))} mi (${removeDecimalPoint(meterToKilometer(navigation.distance_meters))} kms)`}
            />
          </Stack>
          <SuggestedNavigationRouteMap
            navigation={navigation}
            origin={originCoords}
            destination={destinationCoords}
            midPoints={midPointCoords}
          />
          <Typography variant="body1" textAlign="center" sx={{ mt: ".5rem" }}>
            Route distance is approximate and may vary. Please verify as needed.
          </Typography>
        </Stack>
      ) : (
        <Typography
          sx={{ mt: "1rem", mb: "1rem" }}
          textAlign="center"
          variant="body1"
        >
          {isRouteCreatedAfterNavigationRollout(shipment.info.created_at)
            ? "Suggested route not available yet, please check back later."
            : "Suggested route not available for this shipment."}
        </Typography>
      )}
    </Stack>
  );
}
